/**
 * The interview availability light, as drawn in both tools.
 *
 * Green 7+, orange 4–6, red 2–3, black 0–1 bookable slots over today + 7 days,
 * counting only slots where the whole panel is free. Slots that need someone to
 * move a meeting are named next to the light ("+4 with a move") and never
 * change its colour. A role with no panel shows a hollow grey dot: that is a
 * setup gap, and black would read as "nobody is available".
 */
import { cn } from '../../../../shared';
import type { AvailabilityRound, AvailabilityStage } from '../../types/interviewAvailability';
import { LIGHT_DOT, describeStage, isShown, shortLabel, withMoveLabel } from './availabilityCopy';

export function AvailabilityDot({ stage, className }: { stage: AvailabilityStage | null | undefined; className?: string }) {
  const light = stage?.light;
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block h-2.5 w-2.5 flex-none rounded-full',
        light ? LIGHT_DOT[light] : 'border border-solid border-pewter bg-white',
        className
      )}
    />
  );
}

/**
 * "● 9 slots · +4 with a move" — the action board's pill on a role header.
 * Rendered as a plain span: it is a reading, not an action.
 */
export function AvailabilityPill({
  stage,
  round,
  windowEnd,
}: {
  stage: AvailabilityStage | null | undefined;
  round: AvailabilityRound;
  windowEnd: string;
}) {
  if (!isShown(stage)) return null;
  const move = withMoveLabel(stage);
  return (
    <span
      title={describeStage(stage, round, windowEnd)}
      data-testid={`availability-${round.toLowerCase()}`}
      data-light={stage?.light ?? 'none'}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-solid border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700"
    >
      <AvailabilityDot stage={stage} />
      <span className="tabular-nums">{shortLabel(stage, round)}</span>
      {move ? <span className="font-normal text-gray-400 tabular-nums">· {move}</span> : null}
    </span>
  );
}

/**
 * The Radar's interview availability, drawn as a real traffic light (Theresa, Oct 13 2026): one
 * light frame, the role's rounds stacked top to bottom in process order, each with its lamp and
 * its count. Titled "Availability" so it cannot be read as a second set of the phase dots beside
 * it, which carry their own "Bottleneck" title. Rounds the role does not run are left out, so a
 * student role shows two lamps and a full process four. A role with no round to show renders
 * nothing.
 */
export function AvailabilityTrafficLight({
  rounds,
  windowEnd,
}: {
  rounds: Array<{ round: AvailabilityRound; stage: AvailabilityStage | null | undefined }>;
  windowEnd: string;
}) {
  const shown = rounds.filter((r) => isShown(r.stage));
  if (!shown.length) return null;
  const count = (s: AvailabilityStage | null | undefined) => (s?.light && s.free != null ? s.free : '–');
  return (
    <span
      className="flex flex-none flex-col items-center gap-1"
      title={shown.map((r) => describeStage(r.stage, r.round, windowEnd)).join('\n')}
      data-testid="availability-traffic-light"
    >
      <span className="text-[10px] font-semibold text-steel">Availability</span>
      <span className="grid grid-cols-[auto_auto_auto] items-center gap-x-1.5 gap-y-1 rounded-md border border-solid border-pewter bg-white px-2 py-1.5">
        {shown.map((r) => (
          <span key={r.round} className="contents" data-testid={`availability-lamp-${r.round.toLowerCase()}`}>
            <span className="text-[10.5px] text-steel">{r.round}</span>
            <AvailabilityDot stage={r.stage} className="h-2.5 w-2.5" />
            <span className="min-w-[14px] text-right text-[11px] font-semibold tabular-nums text-black">
              {count(r.stage)}
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * The one-line reason inside an opened role, only when the light is red or black — the moment a
 * recruiter needs to act. Green and orange say nothing here; the pill on the header is enough.
 */
export function AvailabilityNote({
  stage,
  round,
  windowEnd,
}: {
  stage: AvailabilityStage | null | undefined;
  round: AvailabilityRound;
  windowEnd: string;
}) {
  if (!isShown(stage) || (stage?.light !== 'red' && stage?.light !== 'black')) return null;
  return (
    <p
      data-testid={`availability-note-${round.toLowerCase()}`}
      className="mb-3 flex items-start gap-2 rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
    >
      <AvailabilityDot stage={stage} className="mt-0.5" />
      <span>
        {describeStage(stage, round, windowEnd)}
        {stage.panelSource === 'configured' && round === 'Case'
          ? ' Adding someone to the pool widens what can be offered.'
          : stage.panelSource === 'link'
            ? ' Adding an interviewer or widening their time windows on the interview link opens more slots.'
            : null}
      </span>
    </p>
  );
}
